import { MAIN_COLLECTION_ID, parseImportUrls, type CollectionId } from '@platewell/core';
import {
  useCollections,
  useConnectionStatus,
  useImportCollectionId,
  usePlatewell,
  useStore,
} from '@platewell/query';
import { MAX_IMPORT_FILES_PER_SESSION } from '@platewell/schema';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useRef, useState, type FormEvent } from 'react';
import { UrlImportField } from './url-import-field.js';
import { quotaRefusal, UpgradeNotice, type QuotaRefusal } from './upgrade-notice.js';
import { createImportSession, createUrlImportSession } from '../lib/api.js';
import { filterImportableRecipeFiles, IMPORT_FILE_ACCEPT } from '../lib/import-files.js';
import { stashLocalImportContents } from '../lib/import-local.js';

type Mode = 'file' | 'url' | 'manual';

interface AddRecipeOptionsProps {
  /**
   * Where the new recipes belong. Left unset — as on the New Recipe page, which is not
   * opened from anywhere in particular — they go to the household's import collection.
   */
  collectionId?: CollectionId;
}

/**
 * The three ways to add recipes: a file, a URL, or by hand. Shared by the New Recipe page
 * and the empty state of a collection, which is the same offer aimed at one collection.
 */
export function AddRecipeOptions({ collectionId }: AddRecipeOptionsProps) {
  const [mode, setMode] = useState<Mode>('file');

  return (
    <>
      <div className="mode-switch" role="tablist" aria-label="How to add a recipe">
        <button
          className={`mode-switch-option ${mode === 'file' ? 'active' : ''}`}
          type="button"
          role="tab"
          aria-selected={mode === 'file'}
          onClick={() => setMode('file')}
        >
          From a file
        </button>
        <button
          className={`mode-switch-option ${mode === 'url' ? 'active' : ''}`}
          type="button"
          role="tab"
          aria-selected={mode === 'url'}
          onClick={() => setMode('url')}
        >
          From a URL
        </button>
        <button
          className={`mode-switch-option ${mode === 'manual' ? 'active' : ''}`}
          type="button"
          role="tab"
          aria-selected={mode === 'manual'}
          onClick={() => setMode('manual')}
        >
          Enter manually
        </button>
      </div>

      {mode === 'file' && <FileImport collectionId={collectionId} />}
      {mode === 'url' && <UrlImport collectionId={collectionId} />}
      {mode === 'manual' && <ManualEntry collectionId={collectionId} />}
    </>
  );
}

function UrlImport({ collectionId }: AddRecipeOptionsProps) {
  const { activeHouseholdId } = usePlatewell();
  const { readOnly, online } = useConnectionStatus();
  const target = useImportTarget(collectionId);
  const navigate = useNavigate();
  const [urlText, setUrlText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<QuotaRefusal | null>(null);

  const canImport = !readOnly && online && activeHouseholdId != null;

  const startUrlImport = useCallback(async () => {
    if (!canImport || activeHouseholdId == null) return;
    const { urls, invalid } = parseImportUrls(urlText);
    if (invalid.length > 0) {
      setError(`Not a recipe URL: ${invalid[0]}`);
      return;
    }
    if (urls.length === 0) {
      setError('Enter a recipe URL to import.');
      return;
    }
    if (urls.length > MAX_IMPORT_FILES_PER_SESSION) {
      setError(`You can import up to ${MAX_IMPORT_FILES_PER_SESSION} URLs at once.`);
      return;
    }
    setBusy(true);
    setError(null);
    setRefusal(null);
    try {
      const session = await createUrlImportSession(activeHouseholdId, urls);
      void navigate({
        to: '/imports/$importId',
        params: { importId: session.id },
        search: { collectionId },
      });
    } catch (cause) {
      // The server counts the whole batch against the plan before opening a session, so a
      // full household is a paywall prompt rather than a failed import.
      const quota = quotaRefusal(cause);
      if (quota != null) setRefusal(quota);
      else setError(cause instanceof Error ? cause.message : 'Could not start URL import');
    } finally {
      setBusy(false);
    }
  }, [activeHouseholdId, canImport, collectionId, navigate, urlText]);

  return (
    <div>
      <p className="hint">
        Paste a link to a recipe — or one link per line — and we&apos;ll pull them in. They&apos;re
        added to the {target} collection.
      </p>
      <ImportAvailability online={online} readOnly={readOnly} />

      {canImport && (
        <div className="field">
          <label className="label" htmlFor="recipe-url">
            Recipe URLs
          </label>
          <UrlImportField
            id="recipe-url"
            testId="recipe-new-url-input"
            value={urlText}
            onChange={setUrlText}
            onSubmit={() => void startUrlImport()}
            busy={busy}
            buttonLabel="Import"
            buttonClassName="btn primary"
            autoFocus
          />
        </div>
      )}

      {refusal != null && <UpgradeNotice refusal={refusal} action="import these recipes" />}
      {error != null && <p className="error-text">{error}</p>}
    </div>
  );
}

function FileImport({ collectionId }: AddRecipeOptionsProps) {
  const { activeHouseholdId } = usePlatewell();
  const { readOnly, online } = useConnectionStatus();
  const target = useImportTarget(collectionId);
  const navigate = useNavigate();
  const filesInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<QuotaRefusal | null>(null);

  const canImport = !readOnly && online && activeHouseholdId != null;

  const startImport = useCallback(
    async (fileList: FileList | File[]) => {
      if (!canImport || activeHouseholdId == null) return;
      const files = filterImportableRecipeFiles(fileList);
      if (files.length === 0) {
        setError('No supported recipe files found (JSON, HTML, DOCX, ODT, PDF, TXT, PNG, JPG, WEBP, AVIF, HEIC).');
        return;
      }
      if (files.length > MAX_IMPORT_FILES_PER_SESSION) {
        setError(`You can import up to ${MAX_IMPORT_FILES_PER_SESSION} files at once.`);
        return;
      }

      setBusy(true);
      setError(null);
      setRefusal(null);
      try {
        const session = await createImportSession(activeHouseholdId, files);
        stashLocalImportContents(
          session.id,
          files,
          session.files.map((file) => file.id),
        );
        void navigate({
          to: '/imports/$importId',
          params: { importId: session.id },
          search: { collectionId },
        });
      } catch (cause) {
        // Same precheck as the URL path: too many files for the remaining slots is a plan
        // problem, not an import problem, and says so with a way out.
        const quota = quotaRefusal(cause);
        if (quota != null) setRefusal(quota);
        else setError(cause instanceof Error ? cause.message : 'Could not start import');
      } finally {
        setBusy(false);
      }
    },
    [activeHouseholdId, canImport, collectionId, navigate],
  );

  function onDrop(event: React.DragEvent) {
    event.preventDefault();
    setDragOver(false);
    if (!canImport) return;
    void startImport(event.dataTransfer.files);
  }

  function onFileInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (files != null) void startImport(files);
    event.target.value = '';
  }

  return (
    <div>
      <p className="hint">
        Import photos, text, or structured data of recipes. They&apos;re added to the {target}{' '}
        collection.
      </p>
      <ImportAvailability online={online} readOnly={readOnly} />

      {canImport && (
        <div
          className={`import-dropzone ${dragOver ? 'active' : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <p>Drop recipe files here</p>
          <p className="hint">JSON, HTML, DOCX, ODT, PDF, TXT, PNG, JPG, WEBP, AVIF, HEIC</p>
          <div className="import-dropzone-actions">
            <button
              className="btn"
              type="button"
              disabled={busy}
              onClick={() => filesInputRef.current?.click()}
            >
              {busy ? 'Starting import…' : 'Choose files'}
            </button>
          </div>
          <input
            ref={filesInputRef}
            className="sr-only"
            type="file"
            accept={IMPORT_FILE_ACCEPT}
            multiple
            data-testid="recipe-new-files-input"
            onChange={onFileInputChange}
          />
        </div>
      )}

      {refusal != null && <UpgradeNotice refusal={refusal} action="import these recipes" />}
      {error != null && <p className="error-text">{error}</p>}
    </div>
  );
}

function ManualEntry({ collectionId }: AddRecipeOptionsProps) {
  const store = useStore();
  const collections = useCollections();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [targetId, setTargetId] = useState(collectionId ?? MAIN_COLLECTION_ID);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<QuotaRefusal | null>(null);

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setRefusal(null);
    try {
      // Created locally and immediately, with no network round-trip: the id is generated
      // here and the recipe is already syncing by the time the editor renders.
      const recipeId = await store.addRecipe({ title: title.trim() }, [targetId]);
      void navigate({ to: '/recipes/$recipeId/edit', params: { recipeId } });
    } catch (cause) {
      setBusy(false);
      // The plan cap is refused here, locally and offline, before anything is written.
      const quota = quotaRefusal(cause);
      if (quota != null) setRefusal(quota);
      else setError(cause instanceof Error ? cause.message : 'Could not create the recipe');
    }
  }

  return (
    <form className="editor" onSubmit={create} style={{ padding: 0 }}>
      <div className="field">
        <label className="label" htmlFor="title">
          Title
        </label>
        <input
          id="title"
          className="input"
          autoFocus
          required
          value={title}
          placeholder="Sunday roast chicken"
          onChange={(event) => setTitle(event.target.value)}
        />
      </div>

      <div className="field">
        <label className="label" htmlFor="collection">
          Collection
        </label>
        <select
          id="collection"
          className="select"
          value={targetId}
          onChange={(event) => setTargetId(event.target.value as CollectionId)}
        >
          {collections.map((collection) => (
            <option key={collection.id} value={collection.id}>
              {collection.name}
            </option>
          ))}
        </select>
      </div>

      <div className="row">
        <button className="btn primary" type="submit" disabled={busy || title.trim() === ''}>
          Create and edit
        </button>
      </div>

      {refusal != null && <UpgradeNotice refusal={refusal} action="add more recipes" />}
      {error != null && <p className="error-text">{error}</p>}
    </form>
  );
}

function ImportAvailability({ online, readOnly }: { online: boolean; readOnly: boolean }) {
  if (!online) {
    return <p className="error-text">Import needs a network connection. Reconnect and try again.</p>;
  }
  if (readOnly) {
    return (
      <p className="hint">You have read-only access in this household and cannot import recipes.</p>
    );
  }
  return null;
}

/** The name of the collection an import will land in: the chosen one, else the household default. */
function useImportTarget(collectionId: CollectionId | undefined): string {
  const collections = useCollections();
  const importCollectionId = useImportCollectionId();
  const id = collectionId ?? importCollectionId;
  return collections.find((collection) => collection.id === id)?.name ?? 'Main';
}
